/**
 * WorkbenchShell — the field-agnostic Evidence Foundry workbench surface.
 *
 * Renders a `WorkbenchManifest` (`field-pack.workbench.v1`): a tab bar + per-tab
 * experiment-family and integration-rail card panels, plus host-injected slots for any
 * field-specific rich content. No field vocabulary lives in the shell — every string comes
 * from the manifest — so this same shell renders any research field's workbench with zero
 * shell code changes. Here it renders HenniPenny's NILM load-disaggregation field.
 *
 * This is a cloned template of the shared Evidence Foundry `field-pack.workbench.v1` shell
 * (deliberately vendored into the lab repo, not imported cross-repo — see docs/HANDOFF).
 */
import { type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { Database, FlaskConical } from 'lucide-react';
import type { InfoCard, WorkbenchCardGroup, WorkbenchManifest } from './types';

const panelStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius-lg)',
  background: 'var(--sp-bg-elev)',
  padding: 'var(--sp-space-4)',
} satisfies CSSProperties;

const subtlePanelStyle = {
  border: '1px solid var(--sp-border)',
  borderRadius: 'var(--sp-radius)',
  background: 'var(--sp-bg)',
  padding: 'var(--sp-space-3)',
} satisfies CSSProperties;

/** Per-tab content the host composes around the manifest-driven card panels. */
export type WorkbenchTabSlot = {
  /** Rendered first, above the experiment-family panel (e.g. a readiness line). */
  leading?: ReactNode;
  /** Rendered last, below the integration rail (e.g. live analysis panels, boundaries). */
  trailing?: ReactNode;
};

export type WorkbenchShellProps = {
  manifest: WorkbenchManifest;
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Rendered between the tab bar and the tab panels (e.g. a parse-error alert). */
  belowTabs?: ReactNode;
  /** Field-specific rich content keyed by tab id. */
  tabSlots?: Record<string, WorkbenchTabSlot>;
};

function SectionTitle({ icon: Icon, eyebrow, title }: { icon: typeof FlaskConical; eyebrow: string; title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 'var(--sp-space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
        <Icon size={18} style={{ color: 'var(--sp-text-muted)', marginTop: 2, flexShrink: 0 }} />
        <div>
          <div className="sp-eyebrow">{eyebrow}</div>
          <h2 className="sp-display" style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 400 }}>
            {title}
          </h2>
        </div>
      </div>
    </div>
  );
}

function InfoCardGrid({ cards }: { cards: InfoCard[] }) {
  return (
    <div className="hp-info-grid">
      {cards.map((card) => (
        <div key={`${card.eyebrow}-${card.title}`} style={subtlePanelStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <div>
              <div className="sp-eyebrow" style={{ fontSize: 9.5 }}>
                {card.eyebrow}
              </div>
              <div style={{ marginTop: 5, fontWeight: 600 }}>{card.title}</div>
            </div>
            {card.status ? (
              <span className="mono" style={{ fontSize: 9.5, color: 'var(--sp-accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {card.status}
              </span>
            ) : null}
          </div>
          <p style={{ margin: '8px 0 0', color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.55 }}>{card.body}</p>
        </div>
      ))}
    </div>
  );
}

function ExperimentFamilyPanel({ group }: { group: WorkbenchCardGroup }) {
  return (
    <section style={panelStyle}>
      <SectionTitle icon={FlaskConical} eyebrow="Experiment types" title={group.title} />
      <p style={{ margin: '-4px 0 14px', color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>{group.body}</p>
      <InfoCardGrid cards={group.cards} />
    </section>
  );
}

function IntegrationRail({ group }: { group: WorkbenchCardGroup }) {
  return (
    <section style={panelStyle}>
      <SectionTitle icon={Database} eyebrow="Integrations in this workflow" title={group.title} />
      <p style={{ margin: '-4px 0 14px', color: 'var(--sp-text-muted)', fontSize: 13, lineHeight: 1.6 }}>{group.body}</p>
      <InfoCardGrid cards={group.cards} />
    </section>
  );
}

function WorkbenchTabs({
  tabs,
  activeTab,
  onChange,
}: {
  tabs: WorkbenchManifest['tabs'];
  activeTab: string;
  onChange: (tabId: string) => void;
}) {
  const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
  const focusTab = (tabId: string) => {
    window.requestAnimationFrame(() => {
      document.getElementById(`workbench-tab-${tabId}`)?.focus();
    });
  };
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabId: string) => {
    const currentIndex = tabs.findIndex((item) => item.id === tabId);
    const lastIndex = tabs.length - 1;
    const nextTab =
      event.key === 'ArrowRight'
        ? tabs[currentIndex === lastIndex ? 0 : currentIndex + 1]?.id
        : event.key === 'ArrowLeft'
          ? tabs[currentIndex <= 0 ? lastIndex : currentIndex - 1]?.id
          : event.key === 'Home'
            ? tabs[0]?.id
            : event.key === 'End'
              ? tabs[lastIndex]?.id
              : null;
    if (!nextTab) return;
    event.preventDefault();
    onChange(nextTab);
    focusTab(nextTab);
  };
  if (!active) return null;
  return (
    <section id="workbench-tabs" style={panelStyle} aria-label="Workbench sections">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <div className="sp-eyebrow">Choose a task</div>
          <h2 className="sp-display" style={{ margin: '5px 0 0', fontSize: 20, fontWeight: 400 }}>
            {active.eyebrow}
          </h2>
          <p style={{ margin: '7px 0 0', color: 'var(--sp-text-muted)', maxWidth: 620, lineHeight: 1.55 }}>
            {active.description}
          </p>
        </div>
        <div className="hp-workbench-tabs" role="tablist" aria-label="Workbench sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              id={`workbench-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`workbench-panel-${tab.id}`}
              className="hp-workbench-tab"
              onClick={() => onChange(tab.id)}
              onKeyDown={(event) => onTabKeyDown(event, tab.id)}
            >
              <span className="mono">{tab.eyebrow}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export function WorkbenchShell({ manifest, activeTab, onTabChange, belowTabs, tabSlots }: WorkbenchShellProps) {
  if (!manifest.tabs.length) {
    return (
      <section style={panelStyle} aria-label={`${manifest.displayName} workbench`}>
        <div className="sp-eyebrow">{manifest.eyebrow}</div>
        <p style={{ margin: '7px 0 0', color: 'var(--sp-text-muted)' }}>
          No workbench sections are configured for this field yet.
        </p>
      </section>
    );
  }
  return (
    <>
      <WorkbenchTabs tabs={manifest.tabs} activeTab={activeTab} onChange={onTabChange} />
      {belowTabs}
      {manifest.tabs.map((tab) => {
        const slot = tabSlots?.[tab.id];
        return (
          <div
            key={tab.id}
            id={`workbench-panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`workbench-tab-${tab.id}`}
            hidden={activeTab !== tab.id}
            style={{ display: activeTab === tab.id ? 'grid' : 'none', gap: 'var(--sp-space-4)' }}
          >
            {slot?.leading}
            {tab.experimentFamily ? <ExperimentFamilyPanel group={tab.experimentFamily} /> : null}
            {tab.integrationRail ? <IntegrationRail group={tab.integrationRail} /> : null}
            {slot?.trailing}
          </div>
        );
      })}
    </>
  );
}
